/**
 * CookieConsent - GDPR/UK-GDPR compliant cookie consent banner
 * 
 * Implements:
 * - Clear opt-in/opt-out choices
 * - Persistent storage of preferences
 * - Conditional loading of analytics
 * - Links to privacy policy
 */
import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'simplifi_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'simplifi_cookie_preferences';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    functional: true,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch (e) {
          console.error('Failed to parse cookie preferences');
        }
      }
    }
  }, []);

  useEffect(() => {
    const open = () => { setShowBanner(true); setShowPreferences(true); };
    window.addEventListener('simplifi:open-cookie-settings', open);
    return () => window.removeEventListener('simplifi:open-cookie-settings', open);
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);

    // Enable/disable analytics based on consent
    if (prefs.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  };

  const enableAnalytics = () => {
    // Re-enable GA4 and PostHog if consented
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    if (window.posthog) {
      window.posthog.opt_in_capturing();
    }
  };

  const disableAnalytics = () => {
    // Disable GA4 and PostHog
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    });
  };

  const acceptNecessaryOnly = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      functional: true,
      marketing: false
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Consent Banner - z-index 40 to stay below form buttons (z-50) */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-40 p-4 md:p-6 bg-[#0a0f0d]/95 backdrop-blur-xl border-t border-white/10 shadow-2xl safe-area-bottom"
        data-testid="cookie-consent-banner"
        role="dialog"
        aria-label="Cookie Consent"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-7xl mx-auto">
          {!showPreferences ? (
            // Main Banner
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-sm md:text-base mb-1">We Value Your Privacy</h3>
                  <p className="text-white/70 text-xs md:text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. 
                    <a href="/privacy" className="text-primary hover:underline ml-1" data-testid="privacy-policy-link">
                      Read our Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreferences(true)}
                  className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
                  data-testid="cookie-preferences-btn"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Preferences
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptNecessaryOnly}
                  className="!bg-transparent border-white/20 !text-white hover:!bg-white/10 text-xs"
                  data-testid="cookie-reject-btn"
                >
                  Necessary Only
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="bg-primary hover:bg-primary/90 text-black font-medium text-xs"
                  data-testid="cookie-accept-all-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold text-base">Cookie Preferences</h3>
                <button 
                  onClick={() => setShowPreferences(false)}
                  className="text-white/50 hover:text-white p-1"
                  aria-label="Close preferences"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Necessary Cookies */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Strictly Necessary</span>
                    <span className="text-xs text-emerald-300 bg-primary/20 px-2 py-0.5 rounded">Always On</span>
                  </div>
                  <p className="text-white/60 text-xs">Essential for the website to function. Cannot be disabled.</p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Analytics</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-analytics-toggle"
                      />
                      <div className="w-9 h-5 bg-white/20 peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-white/60 text-xs">Help us understand how visitors interact with our website.</p>
                </div>

                {/* Functional Cookies */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Functional</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-functional-toggle"
                      />
                      <div className="w-9 h-5 bg-white/20 peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-white/60 text-xs">Remember your preferences and personalisation settings.</p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">Marketing</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-marketing-toggle"
                      />
                      <div className="w-9 h-5 bg-white/20 peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-white/60 text-xs">Used to deliver relevant ads and measure campaign effectiveness.</p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptNecessaryOnly}
                  className="border-white/20 text-white hover:bg-white/10 text-xs"
                >
                  Reject Optional
                </Button>
                <Button
                  size="sm"
                  onClick={savePreferences}
                  className="bg-primary hover:bg-primary/90 text-black font-medium text-xs"
                  data-testid="cookie-save-preferences-btn"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
